<template>
  <lf-modal
    :close="() => emit('close')"
    xs
    :title="$t('DEALS.APPLICATION_LINKS.GENERATE_LINK_TITLE')"
  >
    <template #content>
      <div class="space-y-5 px-6 pb-2">
        <owner-selector
          v-if="showOwnerSelector"
          ref="ownerSelectorRef"
          v-model:selected-owner-id="selectedOwnerId"
        />
        <lf-dropdown-with-api-search
          v-if="showBpTemplateSelector"
          name="borrower_platform_template"
          full-width
          :placeholder="
            $t('INTEGRATIONS.BORROWER_PLATFORM.BORROWER_PLATFORM_TEMPLATE')
          "
          search-term=""
          :value="selectedBorrowerPlatformTemplate?.name || undefined"
          :get-paginated-options="searchBorrowerPlatformTemplates"
          accessor="name"
          sort
          @change="handleBorrowerPlatformTemplateChange"
        />
        <lf-dropdown
          v-if="
            showBpTemplateSelector &&
            selectedBorrowerPlatformTemplate &&
            Object.keys(bpTabDropdownOptions).length
          "
          name="borrower_platform_template_tab"
          :placeholder="`${t('INTEGRATIONS.BORROWER_PLATFORM.BORROWER_PLATFORM_TEMPLATE')} ${t('COMMON.TAB')}`"
          :value="selectedBorrowerPlatformTemplateTab?.id"
          :options="bpTabDropdownOptions"
          :is-loading="isLoadingTabs"
          full-width
          @change="handleTabChange"
        />
        <div v-if="showExpiration">
          <expiration-selector
            v-model:selected-option="selectedExpirationOption"
            v-model:custom-days="customExpirationDays"
            :options="effectiveExpirationOptions"
          />
          <div class="mt-2 text-xs text-gray-400">
            {{ expirationDateText }}
          </div>
        </div>
        <slot name="additional-options" />
        <url-parameters-section
          ref="urlParamsSectionRef"
          v-model:parameters="urlParameters"
        />
      </div>
      <hr class="border-gray-200" />
      <div class="flex justify-end p-4 px-6">
        <primary-button
          :disabled="!canGenerate || isGenerating"
          data-cy="copy-link-button"
          @click="handleGenerateLink"
        >
          <icon-base icon="link-small" class="mr-1" />
          {{ isGenerating ? $t("COMMON.GENERATING") : $t("COMMON.COPY_LINK") }}
        </primary-button>
      </div>
    </template>
  </lf-modal>
</template>

<script setup lang="ts">
import { watchImmediate } from "@vueuse/core";
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { useMutation } from "@tanstack/vue-query";
import { v4 } from "uuid";

import { DEFAULT_EXPIRATION_DAYS } from "@/helpers/constants";
import { formatUserFullName } from "@/helpers/formatting";
import { useClipboardWithMessage } from "@/hooks/clipboard";
import { usePromiseWrapper } from "@/hooks/common";
import { useDealOwners, useDeals } from "@/hooks/deals";
import { useBorrowerPlatformTemplateQuery } from "@/hooks/queries/useBorrowerPlatformTemplates";
import { TabType, TabVisibility } from "@/models/borrowerPlatformTemplates";
import borrowerPlatformTemplateService from "@/services/modules/borrowerPlatformTemplates";

import ExpirationSelector from "@/components/deals/bp-link-generator/ExpirationSelector.vue";
import OwnerSelector from "@/components/deals/bp-link-generator/OwnerSelector.vue";
import UrlParametersSection from "@/components/deals/bp-link-generator/UrlParametersSection.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfModal from "@/components/ui/Modal.vue";
import PrimaryButton from "@/components/ui/buttons/PrimaryButton.vue";
import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";
import LfDropdownWithApiSearch from "@/components/ui/inputs/LfDropdownWithApiSearch.vue";

import type { ButtonGroupOption } from "@/components/ui/ButtonGroup.vue";
import type {
  BorrowerPlatformTemplate,
  Tab,
} from "@/models/borrowerPlatformTemplates";
import type { UrlParameter } from "@/models/urlParameters";

const {
  applicationId,
  initialOwnerId,
  showOwnerSelector = false,
  showBpTemplateSelector = false,
  showExpiration = false,
  expirationOptions: expirationOptionsProp,
  generateAction,
  additionalPayload = {},
  successMessage: successMessageProp,
} = defineProps<{
  applicationId: string;
  initialOwnerId?: string;
  showOwnerSelector?: boolean;
  showBpTemplateSelector?: boolean;
  showExpiration?: boolean;
  expirationOptions?: ButtonGroupOption[];
  generateAction: string;
  additionalPayload?: Record<string, unknown>;
  successMessage?: string;
}>();

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();
const { dispatch } = useStore();
const { client, activeDeal } = useDeals();
const copyToClipboard = useClipboardWithMessage();
const dealOwners = useDealOwners();

const selectedOwnerId = ref<string>(initialOwnerId ?? "");
const selectedExpirationOption = ref<string | number>(DEFAULT_EXPIRATION_DAYS);
const customExpirationDays = ref<number>(DEFAULT_EXPIRATION_DAYS);
const selectedBorrowerPlatformTemplate = ref<BorrowerPlatformTemplate | null>(
  null
);
const selectedBorrowerPlatformTemplateTab = ref<Tab | null>(null);

const selectedTemplateId = computed(
  () => selectedBorrowerPlatformTemplate.value?.id ?? ""
);
const { data: bpTemplateData, isLoading: isLoadingTabs } =
  useBorrowerPlatformTemplateQuery(selectedTemplateId);

const filteredTabs = computed(() => {
  if (!bpTemplateData.value?.content?.tabs) {
    return [];
  }

  return bpTemplateData.value.content.tabs.filter((tab) => {
    if (
      tab.type === TabType.WORKFLOW &&
      tab.workflow_template_ids?.length &&
      activeDeal.value.workflow_template_id &&
      !tab.workflow_template_ids.includes(activeDeal.value.workflow_template_id)
    ) {
      return false;
    }
    if (tab.type === TabType.LOG_OUT) {
      return false;
    }
    return (
      tab.visibility === TabVisibility.ALWAYS ||
      tab.visibility === TabVisibility.AUTHENTICATED
    );
  });
});

const bpTabDropdownOptions = computed(() => {
  return filteredTabs.value.reduce<Record<string, string>>((acc, tab) => {
    acc[tab.id] = tab.title ?? "";
    return acc;
  }, {});
});

const urlParameters = ref<UrlParameter[]>([
  { id: v4(), type: "", customKey: "", value: "" },
]);
const ownerSelectorRef =
  useTemplateRef<InstanceType<typeof OwnerSelector>>("ownerSelectorRef");
const urlParamsSectionRef = useTemplateRef<
  InstanceType<typeof UrlParametersSection>
>("urlParamsSectionRef");

const DEFAULT_OPTIONS: ButtonGroupOption[] = [
  { value: 7, label: `7 ${t("COMMON.DAYS")}` },
  { value: 30, label: `30 ${t("COMMON.DAYS")}` },
  { value: "custom", label: t("COMMON.CUSTOM") },
];

const effectiveExpirationOptions = computed(
  () => expirationOptionsProp || DEFAULT_OPTIONS
);

const effectiveExpirationDays = computed(() => {
  if (selectedExpirationOption.value === "custom") {
    return customExpirationDays.value;
  }
  return Number(selectedExpirationOption.value);
});

const selectedOwner = computed(() =>
  dealOwners.value.find((owner) => String(owner.id) === selectedOwnerId.value)
);

const successText = computed(() => {
  if (successMessageProp) {
    return successMessageProp;
  }
  const fullName = formatUserFullName(selectedOwner.value);
  if (fullName) {
    return t("COMMON.COPIED_LINK_FOR", { name: fullName });
  }
  return t("COMMON.COPIED");
});

const expirationDateText = computed(() => {
  const expirationDate = new Date();
  expirationDate.setDate(
    expirationDate.getDate() + effectiveExpirationDays.value
  );

  const formattedDate = expirationDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const formattedTime = expirationDate.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return t("DEALS.APPLICATION_LINKS.LINK_EXPIRES_ON", {
    date: formattedDate,
    time: formattedTime,
  });
});

const canGenerate = computed(() => {
  if (showOwnerSelector && !selectedOwnerId.value) {
    return false;
  }
  if (urlParamsSectionRef.value?.hasValidationErrors) {
    return false;
  }
  return true;
});

const { fetchWrapper: searchBorrowerPlatformTemplates } = usePromiseWrapper(
  async (params: { page: number; search: string }) => {
    const payload: Record<string, number | string | undefined> = {
      client_id: client.value?.id,
      page: params.page,
    };

    if (params.search?.trim()) {
      payload.search = params.search;
    }

    return await borrowerPlatformTemplateService.getBorrowerPlatformTemplates({
      payload,
    });
  }
);

const handleBorrowerPlatformTemplateChange = (
  template: BorrowerPlatformTemplate
) => {
  selectedBorrowerPlatformTemplate.value = template;
  selectedBorrowerPlatformTemplateTab.value = null;
};

const handleTabChange = (tabId: string | number) => {
  const tab = filteredTabs.value.find(({ id }) => id === String(tabId));
  selectedBorrowerPlatformTemplateTab.value = tab ?? null;
};

const linkParams = computed(() => {
  const params: Record<string, string> = {};
  for (const param of urlParameters.value) {
    if (!param.type || !param.value.trim()) {
      continue;
    }
    const key = param.type === "other" ? param.customKey.trim() : param.type;
    if (!key) {
      continue;
    }
    params[key] = param.value;
  }
  return params;
});

const { mutate: generateLink, isPending: isGenerating } = useMutation({
  mutationFn: async () => {
    const payload: Record<string, unknown> = {
      application_id: applicationId,
      ...additionalPayload,
    };

    if (showOwnerSelector) {
      payload.ownerId = Number(selectedOwnerId.value);
    }

    if (showExpiration) {
      payload.expirationDays = effectiveExpirationDays.value;
    }

    if (showBpTemplateSelector && selectedBorrowerPlatformTemplate.value) {
      payload.borrower_platform_template_id =
        selectedBorrowerPlatformTemplate.value.id;

      if (selectedBorrowerPlatformTemplateTab.value) {
        payload.borrower_platform_template_tab_id =
          selectedBorrowerPlatformTemplateTab.value.id;
      }
    }

    if (Object.keys(linkParams.value).length) {
      payload.link_params = linkParams.value;
    }

    return dispatch(generateAction, payload);
  },
  onSuccess: async (link: string) => {
    await copyToClipboard(link, { success: successText.value });
    emit("close");
  },
});

const handleGenerateLink = () => generateLink();

watchImmediate(ownerSelectorRef, (selector) => {
  if (selector && !selectedOwnerId.value) {
    selectedOwnerId.value = selector.getFirstOwnerId();
  }
});
</script>
